import { Injectable } from "@angular/core";
import { HttpParams } from "@angular/common/http";

import { Observable, of } from "rxjs";
import { catchError, take } from "rxjs/operators";

import { sharedEnvironment } from "../../../environments/shared-environment";
import {
  ListNotificationsParams,
  NotificationPageDto,
  UnreadCountDto
} from "../../models/notifications/notification.interface";
import {
  NotificationPreferencesDto,
  SetNotificationPreferenceDto
} from "../../models/notifications/notification-preferences.interface";
import { HttpBaseService } from "../http/http-base.service";

/**
 * As seis chamadas HTTP do módulo de notificações (DEV-872).
 *
 * `Authorization` e `X-Current-Role` são injetados pelo `customHttpInterceptor` — não montar aqui.
 * O SSE NÃO passa por este serviço: ele usa `fetch`, fica fora dos interceptors e mora no
 * `NotificationsStreamService`, com URL própria (`api/notifications/stream`, sem `/user`).
 */
@Injectable({ providedIn: "root" })
export class NotificationsService extends HttpBaseService {
  protected override getApiEndpoint(): string[] {
    return [sharedEnvironment.serviceUrl, "api", "user", "notifications"];
  }

  /**
   * Página da listagem. Paginação é keyset com cursor opaco, não offset: o `cursor` recebido em
   * `nextCursor` volta byte a byte. `nextCursor` ausente na resposta = última página.
   */
  public list(params: ListNotificationsParams = {}): Observable<NotificationPageDto> {
    let httpParams = new HttpParams();
    // `unreadOnly` default é false no servidor: mandar "false" seria ruído.
    if (params.unreadOnly) {
      httpParams = httpParams.set("unreadOnly", "true");
    }
    if (params.cursor) {
      httpParams = httpParams.set("cursor", params.cursor);
    }
    if (params.limit && params.limit > 0) {
      httpParams = httpParams.set("limit", params.limit.toString());
    }

    return this.http
      .get<NotificationPageDto>(this.buildPath(), { params: httpParams })
      .pipe(take(1));
  }

  /**
   * Contador do badge. Devolve `null` quando a chamada falha — e não `0`, que é contagem legítima.
   * O erro é engolido de propósito: o sino é chrome permanente e uma falha aqui não pode derrubar o
   * shell nem abrir a modal global de erro. Distinguir falha de zero é responsabilidade do consumidor.
   */
  public getUnreadCount(): Observable<UnreadCountDto | null> {
    return this.http.get<UnreadCountDto>(this.buildPath(["unread-count"])).pipe(
      take(1),
      catchError(() => of(null))
    );
  }

  /** Idempotente no backend. O erro propaga para o chamador desfazer o otimismo. */
  public markRead(id: string): Observable<void> {
    return this.http.patch<void>(this.buildPath([id, "read"]), null).pipe(take(1));
  }

  /** Marca TODAS — o backend não oferece "as do filtro visível". */
  public markAllRead(): Observable<void> {
    return this.http.patch<void>(this.buildPath(["read"]), null).pipe(take(1));
  }

  /**
   * A vista é derivada do catálogo pelo papel corrente, que o backend lê do `X-Current-Role`.
   * Trocar de perfil muda a resposta: recarregue.
   */
  public getPreferences(): Observable<NotificationPreferencesDto> {
    return this.http.get<NotificationPreferencesDto>(this.buildPath(["preferences"])).pipe(take(1));
  }

  /**
   * Um interruptor por chamada — é o desenho do backend, para que duas abas abertas não se
   * sobrescrevam. Chave inválida para o papel corrente responde 422.
   */
  public setPreference(body: SetNotificationPreferenceDto): Observable<void> {
    return this.http.patch<void>(this.buildPath(["preferences"]), body).pipe(take(1));
  }
}
